import { showToast } from '../Common/Toast';
import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../utils/supabaseClient';
import { resolvePaymentTerms, DEFAULT_PAYMENT_TERMS } from '../../utils/paymentTerms';
import PaymentTermsSelect from '../Common/PaymentTermsSelect';
import { DEFAULT_TAX_RATE_MAX, DEFAULT_TAX_RATE_RANGE_TEXT, defaultTaxRateError } from '../../utils/defaultTaxRate';
import { markupPercentError } from '../../utils/markupPercent';
import {
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';


// Native <select> popups are drawn by the OS, so they ignore the app's dark
// glassmorphism unless told: color-scheme dark themes the popup itself, and the
// option class themes each row. Without both, the wizard's dropdowns opened as a
// bright white list against a dark form. Same pair QuoteBuilder uses.
const selectOptionClass = 'bg-slate-800 text-gray-100';

const PricingSetupStep = ({ onComplete, onValidationChange, registerSave }) => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricingData, setPricingData] = useState({
    hourly_rate: 75,
    overtime_rate: 112.5,
    emergency_rate: 150,
    material_markup: 25,
    tax_rate: 0,
    payment_terms: DEFAULT_PAYMENT_TERMS
  });

  // Load existing pricing data
  useEffect(() => {
    loadPricingData();
  }, [user?.company_id]);

  const loadPricingData = async () => {
    if (!user?.company_id) return;

    try {
      setLoading(true);

      // Load from canonical settings table
      const { data: settings, error } = await supabase
        .from('settings')
        .select('*')
        .eq('company_id', user.company_id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading pricing data:', error);
      } else if (settings) {
        // Extract pricing from canonical settings
        const baseRate = settings.labor_rate || 75;
        setPricingData({
          hourly_rate: baseRate,
          overtime_rate: baseRate * (settings.overtime_multiplier ?? 1.5),
          emergency_rate: baseRate * (settings.emergency_rate_multiplier ?? 2.0),
          material_markup: settings.material_markup ?? 25, // ?? not || — respect a legit 0%
          tax_rate: settings.default_tax_rate ?? 0, // ?? not || — respect a legit 0%
          // Was reading default_invoice_terms - the free-text terms-and-conditions
          // column (a TextArea in InvoicingSettingsTab, "Payment due within 30 days.
          // Late fees may apply."). This control is a payment-terms picker, so it reads
          // the payment-terms column, like the Advanced wizard's FinancialSetupStep
          // already did. resolvePaymentTerms reads every stored spelling
          // and returns the canonical code (Net 30 when unreadable).
          payment_terms: resolvePaymentTerms(settings.default_payment_terms)
        });
      }
    } catch (error) {
      console.error('Error loading pricing data:', error);
    } finally {
      setLoading(false);
    }
  };

  // company_settings.overtime_multiplier and .emergency_rate_multiplier are numeric(5,2),
  // so 999.99 is the largest ratio either can hold. They were numeric(3,2) — a 9.99 ceiling
  // that refused perfectly ordinary pricing, because these columns store the rate DIVIDED by
  // the hourly rate: $25/hr with a $300 emergency call-out is 12x. Widened to match
  // marketplace_settings.emergency_rate_multiplier, which was already numeric(5,2)
  // (migration 20260911_widen_rate_multipliers).
  //
  // The check stays, at the real boundary. It is no longer expected to fire for any genuine
  // trade pricing; it exists so the failure at the limit is a sentence the contractor can act
  // on rather than a 22003 that loses the whole pricing step.
  const MAX_RATE_MULTIPLE = 999.99;

  // Validate pricing data
  const validatePricing = () => {
    const errors = [];
    const warnings = [];

    if (pricingData.hourly_rate <= 0) {
      errors.push('Hourly rate must be greater than $0');
    }

    // NO "your rate seems low" ADVICE. It used to warn below $50/hr on the grounds that
    // the rate was low "for professional services" -- an assumption about what trade the
    // user is in, made by software that has just met them. A baker, a cleaner, a mobile
    // valeter or anyone in a cheaper region charges what their market pays, and being
    // told on their first run that their own price looks wrong is both presumptuous and
    // useless: there is nothing to act on and nothing being prevented.
    //
    // The tax-rate range below stays, deliberately. That one is bounded by something
    // outside the user -- jurisdictions do not levy 40% sales tax -- so a value outside
    // it is likely a typo. The difference is whether the app can actually know.

    // Board #623. Same shape as the multiplier note below, and the same column family:
    // company_settings.material_markup is numeric(5,2) (verified live 2026-09-19), so 1000
    // overflows and Postgres refuses the whole pricing save with 22003. The field carried a
    // min and no max, so the only feedback was the generic "Failed to save pricing data" on
    // a step that then would not advance — with nothing saying which of the five numbers on
    // it was responsible or what range it had to be in.
    //
    // Deliberately the STORAGE bound, not a judgement about pricing: the comment above is
    // explicit that the app should not tell a contractor their own markup looks wrong. 999.99
    // is simply the largest number the column can hold.
    // The range lives in utils/markupPercent, shared with Settings -> Rates & Pricing.
    const markupError = markupPercentError(pricingData.material_markup, 'Material markup');
    if (markupError) {
      errors.push(markupError);
    }

    // Operator ruling 2026-09-24: 0-20% inclusive, refused above -- not a 15% "typically"
    // warning, and not clamped (see utils/defaultTaxRate).
    const taxError = defaultTaxRateError(pricingData.tax_rate);
    if (taxError) {
      errors.push(taxError);
    }

    // THESE RATES ARE STORED AS MULTIPLES, AND THE COLUMN CANNOT HOLD A BIG ONE.
    // handleComplete does not save the overtime and emergency rates the user typed — it
    // divides each by the hourly rate and writes company_settings.overtime_multiplier and
    // .emergency_rate_multiplier, both numeric(3,2), so anything above 9.99 is rejected by
    // Postgres with 22003 and the ENTIRE pricing save fails. The inputs carry a min and no
    // max, so nothing stopped it: $25/hr with a $300 emergency call-out is 12x, an ordinary
    // thing for a contractor to type.
    //
    // Reported as a validation error rather than silently clamped. Clamping would store a
    // rate the contractor never agreed to and then quote customers from it, which is worse
    // than refusing the number and saying why.
    const baseRate = Math.max(parseFloat(pricingData.hourly_rate) || 1, 1);
    const checkMultiple = (rate, label) => {
      const value = parseFloat(rate);
      if (!Number.isFinite(value) || value <= 0) return;
      if (value / baseRate > MAX_RATE_MULTIPLE) {
        errors.push(
          `${label} can be at most ${MAX_RATE_MULTIPLE}x the hourly rate ` +
          `($${(baseRate * MAX_RATE_MULTIPLE).toFixed(2)} at $${baseRate.toFixed(2)}/hr)`
        );
      }
    };
    checkMultiple(pricingData.overtime_rate, 'Overtime rate');
    checkMultiple(pricingData.emergency_rate, 'Emergency rate');

    const isValid = errors.length === 0;

    onValidationChange?.({
      valid: isValid,
      errors,
      warnings
    });

    return isValid;
  };

  // Validate on data change
  useEffect(() => {
    if (!loading) {
      validatePricing();
    }
  }, [pricingData, loading]);

  const handleInputChange = (field, value) => {
    // Tax above 20 is kept as typed and refused by validatePricing, so handleComplete will
    // not save it. It used to be clamped to 20 here, which stored a rate nobody chose.
    setPricingData(prev => {
      // When the base hourly rate changes, re-derive the Overtime/Emergency dollar fields from the
      // current implied multipliers so the displayed dollars — and the multipliers persisted on save
      // (overtime_rate/hourly_rate) — stay consistent. Without this, changing the base rate silently
      // collapsed the saved multiplier (e.g. 1.5x → 1.18x). (F-QUICK-1)
      if (field === 'hourly_rate') {
        const newHourly = parseFloat(value) || 0;
        const oldHourly = parseFloat(prev.hourly_rate) || 0;
        const otMult = oldHourly > 0 ? (parseFloat(prev.overtime_rate) || 0) / oldHourly : 1.5;
        const emMult = oldHourly > 0 ? (parseFloat(prev.emergency_rate) || 0) / oldHourly : 2.0;
        return {
          ...prev,
          hourly_rate: value,
          overtime_rate: Number((newHourly * otMult).toFixed(2)),
          emergency_rate: Number((newHourly * emMult).toFixed(2))
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleComplete = async () => {
    if (!validatePricing()) return;

    try {
      setSaving(true);

      // Save pricing data to canonical settings table
      const settingsUpdate = {
        default_tax_rate: parseFloat(pricingData.tax_rate),
        labor_rate: parseFloat(pricingData.hourly_rate),
        overtime_multiplier: parseFloat(pricingData.overtime_rate) / Math.max(parseFloat(pricingData.hourly_rate) || 1, 1),
        material_markup: parseFloat(pricingData.material_markup),
        default_payment_terms: pricingData.payment_terms,
        // Derive emergency multiplier if possible
        emergency_rate_multiplier: Math.max(parseFloat(pricingData.emergency_rate) / Math.max(parseFloat(pricingData.hourly_rate) || 1, 1), 0) || 2.0,
        updated_at: new Date().toISOString()
      };

      // Ensure settings row exists
      const { data: existingSettings, error: selErr } = await supabase
        .from('settings')
        .select('company_id')
        .eq('company_id', user.company_id)
        .limit(1);

      if (!existingSettings || existingSettings.length === 0) {
        const { error: insErr } = await supabase
          .from('settings')
          .insert({ company_id: user.company_id, created_at: new Date().toISOString() });
        if (insErr) throw insErr;
      }

      const { error } = await supabase
        .from('settings')
        .update(settingsUpdate)
        .eq('company_id', user.company_id);

      if (error) throw error;

      console.log('✅ Pricing data saved successfully');

      // No catalog/rate-card seeding here (or anywhere in onboarding). The price
      // book and rate cards are company-owned data the contractor builds or
      // imports themselves — industry standard is an empty Price Book with an
      // "Add your first item" CTA, not silently-invented defaults. (This step
      // previously called create_default_rate_cards, which both errored AND was
      // conceptually wrong — seeding a second catalog into the legacy rate_cards
      // table.) Rates the user enters above ARE saved to settings; that's the
      // "Pricing set up ✓" the summary refers to, not a seeded catalog.

      onComplete?.(pricingData);

    } catch (error) {
      console.error('Error saving pricing data:', error);
      showToast('error', 'Failed to save pricing data. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Same defect as F-ATLAS-0812-1609-STEP1-NO-PATCH, found while auditing all five steps:
  // handleComplete persists the pricing rows to `settings` (:145/:152) and only then calls
  // onComplete, but it was never registered — so the wizard's footer "Continue" hit
  // goNext()'s handleStepComplete() fallback and advanced WITHOUT writing labor_rate /
  // default_tax_rate / material_markup. Keep handleComplete unmemoized in the deps; see the
  // ordering note in CompanyBasicsStep.js before changing this.
  useEffect(() => { registerSave?.(handleComplete); }, [registerSave, handleComplete]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 [color-scheme:dark]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Set Your Pricing</h2>
        <p className="text-gray-600">
          Configure your rates so you can start creating quotes immediately
        </p>
      </div>

      {/* Pricing Form */}
      <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border p-6 space-y-6">
        {/* Labor Rates */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <ClockIcon className="w-5 h-5" />
            Labor Rates
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="pricing-hourly-rate" className="block text-sm font-medium text-gray-700 mb-1">
                Standard Hourly Rate *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  id="pricing-hourly-rate"
                  type="number"
                  min="1"
                  step="0.01"
                  value={pricingData.hourly_rate}
                  onChange={(e) => handleInputChange('hourly_rate', e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="75.00"
                />
              </div>
            </div>

            <div>
              <label htmlFor="pricing-overtime-rate" className="block text-sm font-medium text-gray-700 mb-1">
                Overtime billing rate
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  id="pricing-overtime-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={pricingData.overtime_rate}
                  onChange={(e) => handleInputChange('overtime_rate', e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="112.50"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Usually 1.5x standard rate. Applied to the customer's labor rate for overtime hours. Employee overtime pay is set per employee and is for tracking only.</p>
            </div>

            <div>
              <label htmlFor="pricing-emergency-rate" className="block text-sm font-medium text-gray-700 mb-1">
                Emergency Rate
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  id="pricing-emergency-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={pricingData.emergency_rate}
                  onChange={(e) => handleInputChange('emergency_rate', e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="150.00"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">After hours/weekends</p>
            </div>
          </div>
        </div>

        {/* Material Markup & Tax */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="pricing-material-markup" className="block text-sm font-medium text-gray-700 mb-1">
              Material Markup (optional)
            </label>
            <div className="relative">
              <input
                id="pricing-material-markup"
                type="number"
                min="0"
                step="0.1"
                value={pricingData.material_markup}
                onChange={(e) => handleInputChange('material_markup', e.target.value)}
                className="w-full pr-8 pl-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="25"
              />
              <span className="absolute right-3 top-2 text-gray-500">%</span>
            </div>
            {/* Said to be optional, and what 0 means, because this field is shown to every
                business on both brands -- including plenty that never resell a part. It
                is NOT defaulted to 0: for a trade contractor a silent 0% markup means
                under-charging on every material, which is a worse failure than showing a
                field somebody can ignore. Whether the DEFAULT should differ per brand is
                a pricing decision for the operator, not a copy change. */}
            <p className="text-xs text-gray-500 mt-1">
              What you add on parts you buy for a job. Set it to 0 if you don&apos;t resell materials.
            </p>
          </div>

          <div>
            <label htmlFor="pricing-tax-rate" className="block text-sm font-medium text-gray-700 mb-1">
              Tax Rate
            </label>
            <div className="relative">
              <input
                id="pricing-tax-rate"
                type="number"
                min="0"
                max={DEFAULT_TAX_RATE_MAX}
                step="0.01"
                value={pricingData.tax_rate}
                aria-invalid={!!defaultTaxRateError(pricingData.tax_rate)}
                aria-describedby={defaultTaxRateError(pricingData.tax_rate) ? 'pricing-tax-rate-error' : undefined}
                onChange={(e) => handleInputChange('tax_rate', e.target.value)}
                className="w-full pr-8 pl-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="0.00"
              />
              <span className="absolute right-3 top-2 text-gray-500">%</span>
            </div>
            {/* The range is stated even when nothing is wrong. `max="20"` on a number input
                does not stop typing — it only marks the field :invalid for native constraint
                validation, which never runs because Continue is not a native form submit. So
                the limit existed and the only way to discover it was to hit it. */}
            <p className="text-xs text-gray-500 mt-1">Local sales tax rate — {DEFAULT_TAX_RATE_RANGE_TEXT}</p>
            {defaultTaxRateError(pricingData.tax_rate) && (
              <p id="pricing-tax-rate-error" role="alert" className="mt-1 text-xs text-red-400">
                {defaultTaxRateError(pricingData.tax_rate)}
              </p>
            )}
          </div>
        </div>

        {/* Payment Terms */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Default Payment Terms
          </label>
          {/* The one terms picker (utils/paymentTerms), so this cannot drift from what the
              due-date calculation understands. */}
          <PaymentTermsSelect
            value={pricingData.payment_terms}
            onChange={(code) => handleInputChange('payment_terms', code)}
            optionClassName={selectOptionClass}
            className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-green-500 [color-scheme:dark]"
          />
        </div>
      </div>

      {/* Continue Button */}
      <div className="text-center mt-8">
        {/* The in-form "Continue - Ready to Quote!" button was removed. It duplicated the
            wizard footer, which runs this exact handler via registerSave -- and its label
            promised something the product cannot honour at this point: after Quick Start
            step 2 the company has rates but no customers and no price book, so nobody is
            "ready to quote" yet. The footer says Complete Setup, which is true. */}
        
        <div className="text-sm text-gray-500 mt-2">
          You can adjust these rates anytime in Settings
        </div>
      </div>
    </div>
  );
};

export default PricingSetupStep;


